/**
 * Address Entry Page - Address collection step
 *
 * Screen for entering address with search functionality and manual entry option
 * Part of the sequential flow: Calculator → Phone → Address → ID → Review → Status
 */

import { t } from "@lingui/core/macro";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, MapPin } from "lucide-react";
import { useEffect, useId, useState } from "react";
import { CircleFlag } from "react-circle-flags";
import { z } from "zod";
import { RampsButton } from "@/components/ramps/Button";
import { useApiClientWithAutoVersion } from "@/hooks/useApiClientWithAutoVersion";
import { useContinue } from "@/hooks/useContinue";
import { FEATURE_FLAGS, useFeatureFlags } from "@/hooks/useFeatureFlags";
import { useNavigateBack } from "@/hooks/useNavigateBack";
import { usePlacesAutocomplete } from "@/hooks/usePlacesAutocomplete";
import { useAnalytics } from "@/hooks/usePostHog";
import { getAuthTokenFromSearchParams } from "@/lib/ramps-search-schema";
import { extractAddressComponents } from "@/utils/google-places-autocomplete";
import { RampsInput, RampsSelect } from "../components/ramps/Input";
import { RampsText } from "../components/ramps/Text";
import { Progress } from "../components/ui/progress";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
} from "../components/ui/select";
import {
	findSubdivisionByRegionName,
	getSubdivisions,
} from "../constants/subdivisions";
import { rampsActions, useRampsStore } from "../stores/use-ramps-store";

interface CountryOption {
	value: string;
	label: string;
	countryCode: string;
}

// Zod schema for address validation with custom error messages
const createAddressSchema = () => {
	return z.object({
		address: z
			.string()
			.min(1, t`Address is required`)
			.max(30, t`Address must be 30 characters or less`)
			.regex(
				/^([a-zA-Z0-9 \u00C0-\u017F#/."',()-])*$/,
				t`Only alpha, numeric and certain special characters (#./'-,()") are allowed`,
			),
		address2: z
			.string()
			.max(80, t`Address Line 2 must be 80 characters or less`)
			.regex(
				/^([a-zA-Z0-9 \u00C0-\u017F#/."',()-])*$/,
				t`Only alpha, numeric and certain special characters (#./'-,()") are allowed`,
			)
			.optional()
			.or(z.literal("")),
		city: z
			.string()
			.min(1, t`City is required`)
			.max(40, t`City must be 40 characters or less`)
			.regex(
				/^([a-zA-Z \u00C0-\u017F#/."',()-])*$/,
				t`Only alpha and certain special characters (#./'-,()") are allowed`,
			),
		zipCode: z
			.string()
			.min(2, t`Postal code must be at least 2 characters`)
			.max(10, t`Postal code must be 10 characters or less`)
			.regex(
				/^([0-9a-zA-Z -])*$/,
				t`Only alphanumeric characters, spaces, and hyphens are allowed`,
			),
		state: z.string().optional(),
	});
};

function AddressPage() {
	const search = Route.useSearch(); // Get current search params
	const store = useRampsStore();
	const handleContinue = useContinue();
	const handleBack = useNavigateBack();
	const { trackPageView } = useAnalytics();

	// Track page view
	useEffect(() => {
		trackPageView("address", { page_name: "address" });
	}, [trackPageView]);

	// Generate unique IDs for form fields
	const addressId = useId();
	const address2Id = useId();
	const cityId = useId();
	const zipCodeId = useId();

	// Get API client with authentication (supports both v2 and v5 API)
	const authToken = getAuthTokenFromSearchParams(search) || store.token;
	const { apiClient } = useApiClientWithAutoVersion({
		authToken,
	});

	// Fetch countries using same logic as calculator and phone
	const countriesQuery = useQuery({
		queryKey: ["countries"],
		queryFn: async () => {
			const countries = await apiClient.referenceData.getCountries();
			return countries;
		},
		enabled: false,
	});

	// Fetch service options for allowed countries using same logic as calculator
	const serviceOptionsQuery = useQuery({
		queryKey: [
			"serviceOptions",
			store.operation,
			store.assetCode,
			store.payoutCountry,
		],
		queryFn: () =>
			apiClient.serviceOptions.getServiceOptions({
				operation: store.operation || "withdraw",
				assetCode: store.assetCode,
				userSelectedCountry: store.payoutCountry,
			}),
		enabled: false,
	});

	// Transform API data for address country selection using same logic as calculator
	const allCountries = countriesQuery.data || [];
	const allowedCountryCodes = serviceOptionsQuery.data?.cashOutCountries || [];
	const filteredCountries =
		allowedCountryCodes.length > 0
			? allCountries.filter((country) =>
					allowedCountryCodes.includes(country.iso3Code),
				)
			: allCountries;

	const countryOptions: CountryOption[] = filteredCountries.map((country) => ({
		value: country.iso3Code,
		label: country.name,
		countryCode: country.iso2Code.toLowerCase(),
	}));

	// Check feature flag for address lookup
	const { isFeatureEnabled } = useFeatureFlags();
	const isAddressLookupEnabled = isFeatureEnabled(
		FEATURE_FLAGS.RAMPS_ADDRESS_LOOKUP_ENABLED,
	);

	// Determine default mode: if address fields exist, default to manual; otherwise autocomplete
	// If feature flag is disabled, always use manual mode
	const hasExistingAddress =
		store.address || store.city || store.zipCode || store.addressCountry;
	const [mode, setMode] = useState<"autocomplete" | "manual">(
		!isAddressLookupEnabled || hasExistingAddress ? "manual" : "autocomplete",
	);

	// State variables
	const [address, setAddress] = useState(store.address);
	const [address2, setAddress2] = useState(store.address2);
	const [city, setCity] = useState(store.city);
	const [zipCode, setZipCode] = useState(store.zipCode);
	const [country, setCountry] = useState(
		store.addressCountry || store.payoutCountry,
	);
	const [state, setState] = useState(store.addressState);
	const [validationErrors, setValidationErrors] = useState<
		Record<string, string>
	>({});

	const selectedCountry = countryOptions.find((c) => c.value === country);

	// Get ISO2 country code for autocomplete region bias
	const selectedCountryISO2 = selectedCountry?.countryCode.toUpperCase();

	// Places autocomplete hook - only initialize if feature flag is enabled
	const {
		inputValue,
		setInputValue,
		suggestions,
		isLoading: isAutocompleteLoading,
		error: autocompleteError,
		selectPlace,
		clear: clearAutocomplete,
	} = usePlacesAutocomplete({
		regionCode: selectedCountryISO2,
		languageCode: "en",
		minInputLength: 3,
		maxSuggestions: 5,
	});

	// Countries that require state selection
	const countriesWithStates = ["USA", "CAN", "MEX"];
	const shouldShowState = countriesWithStates.includes(country);

	// Helper function to prevent numbers in text inputs
	const preventNumbers = (value: string) => {
		return value.replace(/[0-9]/g, "");
	};

	// Real-time validation for address fields
	const validateField = (fieldName: string, value: string) => {
		try {
			const addressSchema = createAddressSchema();
			const fieldSchema =
				addressSchema.shape[fieldName as keyof typeof addressSchema.shape];
			if (fieldSchema) {
				fieldSchema.parse(value);
				// Clear error if validation passes
				setValidationErrors((prev) => {
					const newErrors = { ...prev };
					delete newErrors[fieldName];
					return newErrors;
				});
			}
		} catch (error) {
			if (error instanceof z.ZodError) {
				setValidationErrors((prev) => ({
					...prev,
					[fieldName]: error.issues[0]?.message || t`Invalid value`,
				}));
			}
		}
	};

	// Clear state when switching to a country that doesn't require states
	useEffect(() => {
		if (!shouldShowState) {
			setState("");
		}
	}, [shouldShowState]); // Trigger when shouldShowState changes

	const handleSubmit = () => {
		// Clear previous validation errors
		setValidationErrors({});

		// Validate with Zod schema (created fresh to get latest translations)
		const addressSchema = createAddressSchema();
		const validationData = {
			address,
			address2: address2 || undefined,
			city,
			zipCode,
			state: shouldShowState ? state : undefined,
		};

		const result = addressSchema.safeParse(validationData);

		if (!result.success) {
			// Set validation errors
			const errors: Record<string, string> = {};
			result.error.issues.forEach((err: z.ZodIssue) => {
				if (err.path[0]) {
					errors[err.path[0].toString()] = err.message;
				}
			});
			setValidationErrors(errors);
			return;
		}

		// Validate required fields - state is only required for certain countries
		const isValidForm =
			address && city && zipCode && (shouldShowState ? state : true);

		if (!isValidForm) {
			return;
		}

		// Save and continue with the validated data
		saveAndContinue({
			address,
			address2,
			city,
			zipCode,
			addressCountry: country,
			addressState: shouldShowState ? state : "",
		});
	};

	const saveAndContinue = (addressData: {
		address: string;
		address2?: string;
		city: string;
		zipCode: string;
		addressCountry: string;
		addressState: string;
	}) => {
		rampsActions.setAddressData({
			...addressData,
			isManualAddress: mode === "manual",
		});
		rampsActions.setCurrentStep("date-of-birth");

		setTimeout(() => {
			handleContinue(search);
		}, 2000);
	};

	const handleCountryChange = (value: string) => {
		setCountry(value);
		setState(""); // Clear state when country changes
	};

	// Handle place selection from autocomplete
	const handlePlaceSelect = async (
		prediction: Parameters<typeof selectPlace>[0],
	) => {
		try {
			const placeDetails = await selectPlace(prediction);
			if (!placeDetails) {
				return;
			}

			// Extract address components
			const extractedAddress = extractAddressComponents(
				placeDetails,
				allCountries,
				findSubdivisionByRegionName,
			);

			if (!extractedAddress) {
				console.error("Failed to extract address components");
				return;
			}

			// Save directly to store
			rampsActions.setAddressData({
				address: extractedAddress.address,
				address2: extractedAddress.address2,
				city: extractedAddress.city,
				zipCode: extractedAddress.zipCode,
				addressCountry: extractedAddress.addressCountry,
				addressState: extractedAddress.addressState,
				isManualAddress: false,
			});

			// Also update local state for Continue button validation
			setAddress(extractedAddress.address);
			setAddress2(extractedAddress.address2 || "");
			setCity(extractedAddress.city);
			setZipCode(extractedAddress.zipCode);
			setCountry(extractedAddress.addressCountry);
			setState(extractedAddress.addressState);

			// Clear validation errors
			setValidationErrors({});

			// Clear autocomplete input but stay in autocomplete mode
			clearAutocomplete();
		} catch (error) {
			console.error("Error selecting place:", error);
		}
	};

	// Switch to manual mode
	const switchToManualMode = () => {
		setMode("manual");
		clearAutocomplete();
	};

	// Switch to autocomplete mode
	const switchToAutocompleteMode = () => {
		setMode("autocomplete");
		clearAutocomplete();
	};

	return (
		<div className="flex flex-col min-h-screen max-w-screen-sm mx-auto bg-ramps-background-base">
			{/* Header */}
			<div className="flex items-center justify-between p-4">
				<button
					type="button"
					onClick={handleBack}
					className="w-8 h-8 flex items-center justify-center hover:opacity-80 transition-opacity"
					aria-label="Back"
				>
					<ArrowLeft className="w-6 h-6 text-ramps-text-primary" />
				</button>
			</div>

			{/* Progress bar */}
			<div className="px-4 mb-8">
				<Progress
					value={60}
					className="h-1 bg-ramps-background-surface-2 *:bg-ramps-text-primary"
				/>
			</div>

			{/* Content */}
			<div className="flex-1 px-4 flex flex-col">
				<div className="text-left mb-8">
					<RampsText variant="title-lg-bold" color="primary" className="mb-2">
						{t`Your address`}
					</RampsText>
				</div>

				{/* Autocomplete Mode */}
				{mode === "autocomplete" && isAddressLookupEnabled && (
					<div className="space-y-4 flex-1">
						{/* Search Input */}
						<RampsInput
							type="text"
							label={t`Enter your address`}
							value={inputValue}
							onChange={(e) => setInputValue(e.target.value)}
							error={autocompleteError?.message}
						/>

						{/* Show selected address if one was chosen */}
						{address && city && zipCode && !inputValue && (
							<div className="bg-ramps-background-surface-2 rounded-lg px-4 py-3">
								<RampsText variant="body-sm" color="secondary" className="mb-1">
									{t`Selected address`}
								</RampsText>
								<RampsText variant="body-default" color="primary">
									{address}
									{address2 && `, ${address2}`}
									{`, ${city}, ${zipCode}`}
									{state &&
										`, ${getSubdivisions(country).find((s) => s.value === state)?.label || state}`}
								</RampsText>
							</div>
						)}

						{/* Suggestions List */}
						{inputValue.length >= 3 && (
							<div className="space-y-2">
								{isAutocompleteLoading && (
									<div className="px-4 py-2">
										<RampsText variant="body-default" color="secondary">
											{t`Loading suggestions...`}
										</RampsText>
									</div>
								)}
								{!isAutocompleteLoading && suggestions.length > 0 && (
									<div className="bg-ramps-background-base rounded-lg border border-ramps-border-default divide-y divide-ramps-border-default">
										{suggestions.map((suggestion) => (
											<button
												key={suggestion.place_id}
												type="button"
												onClick={() => handlePlaceSelect(suggestion)}
												className="w-full px-4 py-3 text-left hover:bg-ramps-background-surface-2 transition-colors flex items-center gap-3"
											>
												<MapPin className="w-5 h-5 text-ramps-text-secondary shrink-0" />
												<div className="flex-1 min-w-0">
													<RampsText
														variant="body-default"
														color="primary"
														className="font-medium"
													>
														{suggestion.structured_formatting?.main_text ||
															suggestion.description}
													</RampsText>
													{suggestion.structured_formatting?.secondary_text && (
														<RampsText
															variant="body-sm"
															color="secondary"
															className="mt-1"
														>
															{suggestion.structured_formatting.secondary_text}
														</RampsText>
													)}
												</div>
											</button>
										))}
									</div>
								)}
								{!isAutocompleteLoading &&
									inputValue.length >= 3 &&
									suggestions.length === 0 && (
										<div className="px-4 py-2">
											<RampsText variant="body-default" color="secondary">
												{t`No addresses found`}
											</RampsText>
										</div>
									)}
							</div>
						)}

						{/* Manual Entry Option */}
						{isAddressLookupEnabled && (
							<div className="pt-4 text-center">
								<button
									type="button"
									onClick={switchToManualMode}
									className="text-ramps-text-primary font-medium hover:underline"
								>
									<RampsText variant="body-default" color="primary">
										{t`Enter address manually`}
									</RampsText>
								</button>
							</div>
						)}
					</div>
				)}

				{/* Manual Address Entry Mode */}
				{mode === "manual" && (
					<div className="space-y-6 flex-1">
						{/* Switch to Autocomplete Mode Button */}
						{isAddressLookupEnabled && (
							<div className="mb-4 text-center">
								<button
									type="button"
									onClick={switchToAutocompleteMode}
									className="text-ramps-text-primary font-bold underline hover:no-underline"
								>
									<RampsText
										variant="body-default"
										color="primary"
										className="font-bold underline"
									>
										{t`Use address lookup`}
									</RampsText>
								</button>
							</div>
						)}
						<div className="space-y-4">
							{/* Address Line 1 */}
							<RampsInput
								id={addressId}
								type="text"
								label={t`Address Line 1`}
								value={address}
								onChange={(e) => {
									const newValue = e.target.value;
									setAddress(newValue);
									validateField("address", newValue);
								}}
								error={validationErrors.address}
							/>

							{/* Address Line 2 (Optional) */}
							<RampsInput
								id={address2Id}
								type="text"
								label={t`Address Line 2 (Optional)`}
								value={address2}
								onChange={(e) => {
									const newValue = e.target.value;
									setAddress2(newValue);
									validateField("address2", newValue || "");
								}}
								error={validationErrors.address2}
							/>

							{/* City */}
							<RampsInput
								id={cityId}
								type="text"
								label={t`City`}
								value={city}
								onChange={(e) => {
									const newValue = preventNumbers(e.target.value);
									setCity(newValue);
									validateField("city", newValue);
								}}
								error={validationErrors.city}
							/>

							{/* Postal code */}
							<RampsInput
								id={zipCodeId}
								type="text"
								label={t`Postal code`}
								value={zipCode}
								onChange={(e) => {
									const newValue = e.target.value;
									setZipCode(newValue);
									validateField("zipCode", newValue);
								}}
								error={validationErrors.zipCode}
							/>

							{/* Country */}
							<RampsSelect label={t`Country`}>
								<Select value={country} onValueChange={handleCountryChange}>
									<SelectTrigger className="w-full border-none bg-transparent p-0 h-auto shadow-none">
										<div className="flex items-center gap-3">
											<RampsText
												variant="body-default"
												color="primary"
												className="font-medium"
											>
												{selectedCountry?.label}
											</RampsText>
										</div>
									</SelectTrigger>
									<SelectContent className="bg-ramps-background-base border-ramps-border-default">
										{countriesQuery.isLoading ? (
											<SelectItem value="loading" disabled>
												<RampsText variant="body-default" color="secondary">
													{t`Loading countries...`}
												</RampsText>
											</SelectItem>
										) : countriesQuery.error ? (
											<SelectItem value="error" disabled>
												<RampsText variant="body-default" color="secondary">
													{t`Error loading countries`}
												</RampsText>
											</SelectItem>
										) : countryOptions.length === 0 ? (
											<SelectItem value="no-countries" disabled>
												<RampsText variant="body-default" color="secondary">
													{t`No countries available`}
												</RampsText>
											</SelectItem>
										) : (
											countryOptions.map((countryOption) => (
												<SelectItem
													key={countryOption.value}
													value={countryOption.value}
												>
													<div className="flex items-center gap-3">
														<CircleFlag
															countryCode={countryOption.countryCode}
															className="w-6 h-6"
														/>
														<RampsText variant="body-default" color="primary">
															{countryOption.label}
														</RampsText>
													</div>
												</SelectItem>
											))
										)}
									</SelectContent>
								</Select>
							</RampsSelect>

							{/* State - Only show for countries that have states */}
							{shouldShowState && (
								<RampsSelect label={t`State`}>
									<Select value={state} onValueChange={setState}>
										<SelectTrigger className="w-full border-none bg-transparent p-0 h-auto shadow-none">
											<RampsText
												variant="body-default"
												color="primary"
												className="font-medium"
											>
												{getSubdivisions(country).find((s) => s.value === state)
													?.label || t`Select state`}
											</RampsText>
										</SelectTrigger>
										<SelectContent className="bg-ramps-background-base border-ramps-border-default">
											{getSubdivisions(country).map((stateOption) => (
												<SelectItem
													key={stateOption.value}
													value={stateOption.value}
												>
													<RampsText variant="body-default" color="primary">
														{stateOption.label}
													</RampsText>
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</RampsSelect>
							)}
						</div>
					</div>
				)}

				{/* Continue Button */}
				<div className="pt-8 pb-6">
					<RampsButton
						onClick={handleSubmit}
						disabled={
							!address ||
							!city ||
							!zipCode ||
							(shouldShowState && !state) ||
							Object.keys(validationErrors).length > 0
						}
						className="w-full h-12 rounded-lg font-medium bg-black text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
					>
						{t`Continue`}
					</RampsButton>
				</div>
			</div>
		</div>
	);
}

export const Route = createFileRoute("/ramps/address")({
	validateSearch: (search) =>
		search as {
			step?: string;
			transaction_id?: string;
			token?: string;
			accesstoken?: string;
		},
	component: AddressPage,
});
